import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "./ModelFallbackSection";
import { DialogPanel } from "../shared/DialogPanel";
import { XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

// Seção "Estoque" da workstation: tools nativas de estoque (kind="estoque").
// O gestor preenche só campos — a lógica (busca/detalhe/fotos + cache Gemini por
// loja) mora no backend. O template padrão (painel Dev) pré-preenche a criação.

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
}

interface EstoqueForm {
  name: string;
  description: string;
  loja: string;
  criterio: string;
  selecionar: string;
  detalhe: string;
  apresentar: string;
  instrucoes: string;
  timeout: number | null;
  model_provider_override: string | null;
  model_name_override: string | null;
  model_temperature_override: number | null;
  model_reasoning_effort_override: string | null;
  model_fallback: ModelSelectorValue | null;
  cooldown_seconds: number | null;
  cooldown_message: string;
  qualifies_human: boolean;
  ends_followup: boolean;
  pause_minutes: number | null;
  // Só em modo template: tool global (campos editáveis pelo cliente/gestor) + nota de versão.
  is_global: boolean;
  editable_fields: string[];
  version_note: string;
}

// Caminhos que um template global de estoque pode liberar (params.loja normalmente fica
// fixo em {{ sistema.estoque }}; o cliente conecta o estoque pelo portal).
const ESTOQUE_EDITABLE: { path: string; label: string }[] = [
  { path: "params.loja", label: "Loja (slug)" },
  { path: "prompts.criterio", label: "Prompt critério" }, { path: "prompts.selecionar", label: "Prompt selecionar" },
  { path: "prompts.detalhe", label: "Prompt detalhe" }, { path: "prompts.apresentar", label: "Prompt apresentar" },
  { path: "prompts.instrucoes", label: "Prompt instruções" }, { path: "timeout", label: "Timeout" },
  { path: "cooldown_seconds", label: "Cooldown" }, { path: "cooldown_message", label: "Mensagem de cooldown" },
];

function formFrom(tool?: any, template?: any): EstoqueForm {
  const src = tool ?? template;
  const p = (src?.prompts ?? {}) as Record<string, string>;
  return {
    name: tool?.name ?? "",
    description: src?.description ?? "",
    loja: tool?.params?.loja ?? "",
    criterio: p.criterio ?? "",
    selecionar: p.selecionar ?? "",
    detalhe: p.detalhe ?? "",
    apresentar: p.apresentar ?? "",
    instrucoes: p.instrucoes ?? "",
    timeout: src?.timeout ?? null,
    model_provider_override: src?.model_provider_override ?? null,
    model_name_override: src?.model_name_override ?? null,
    model_temperature_override: src?.model_temperature_override ?? null,
    model_reasoning_effort_override: src?.model_reasoning_effort_override ?? null,
    model_fallback: fallbackToForm(src?.model_fallback),
    cooldown_seconds: src?.cooldown_seconds ?? null,
    cooldown_message: src?.cooldown_message ?? "",
    qualifies_human: src?.qualifies_human ?? false,
    ends_followup: src?.ends_followup ?? false,
    pause_minutes: typeof src?.pause_minutes === "number" && src.pause_minutes > 0 ? src.pause_minutes : null,
    is_global: Array.isArray(src?.editable_fields),
    editable_fields: Array.isArray(src?.editable_fields) ? src.editable_fields : [],
    version_note: "",
  };
}

const PROMPT_FIELDS: { key: keyof EstoqueForm; label: string; hint: string; rows: number }[] = [
  { key: "criterio", label: "Critério", hint: "Instrução que o agente principal lê pra preencher o parâmetro da tool (as 3 funções: busca, ficha, fotos).", rows: 6 },
  { key: "selecionar", label: "Selecionar", hint: "Regras de seleção sobre o estoque. Com modelo Gemini, este prompt + o estoque da loja entram no cache compartilhado.", rows: 8 },
  { key: "detalhe", label: "Detalhe", hint: "Como responder perguntas sobre a ficha de um veículo.", rows: 4 },
  { key: "apresentar", label: "Apresentar", hint: "Formato do output devolvido ao agente principal após a seleção.", rows: 6 },
  { key: "instrucoes", label: "Instruções de apresentação", hint: "Bloco fixo anexado ao final da resposta (como montar a apresentação no WhatsApp).", rows: 6 },
];

export function EstoqueToolModal({
  agentId, tool, template, templateMode = false, onClose,
}: {
  agentId?: string;
  tool?: any;
  template?: any;
  templateMode?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<EstoqueForm>(() => formFrom(tool, template));
  const [formError, setFormError] = useState("");
  const snapRef = useRef<string | null>(null);
  const snapshot = JSON.stringify(form);
  if (snapRef.current === null) snapRef.current = snapshot;
  const dirty = snapshot !== snapRef.current;
  const [confirmClose, setConfirmClose] = useState(false);
  const requestClose = () => { if (dirty) setConfirmClose(true); else onClose(); };

  const { data: modelCatalog = [] } = useQuery<AvailableModel[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const overrideOn = form.model_provider_override !== null && !!form.model_name_override;

  function payload() {
    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      role: "agent",
      kind: "estoque",
      method: "POST",
      url: null,
      headers: {},
      params: { loja: form.loja.trim() },  // template também guarda a loja (ex.: {{ sistema.estoque }}); import copia
      body: {},
      body_type: "json",
      timeout: form.timeout,
      code: null,
      params_schema: null,
      param_values: null,
      model_provider_override: overrideOn ? form.model_provider_override : null,
      model_name_override: overrideOn ? form.model_name_override : null,
      model_temperature_override: overrideOn ? form.model_temperature_override : null,
      model_reasoning_effort_override: overrideOn ? form.model_reasoning_effort_override : null,
      model_fallback: overrideOn ? formToFallback(form.model_fallback) : null,
      prompts: {
        criterio: form.criterio,
        selecionar: form.selecionar,
        detalhe: form.detalhe,
        apresentar: form.apresentar,
        instrucoes: form.instrucoes,
      },
      cooldown_seconds: form.cooldown_seconds,
      cooldown_message: form.cooldown_message.trim() || null,
      qualifies_human: form.qualifies_human,
      ends_followup: form.ends_followup,
      pause_minutes: form.pause_minutes || null,
      ...(templateMode ? {
        editable_fields: form.is_global ? form.editable_fields : null,
        ...(tool && form.is_global ? { version_note: form.version_note.trim() } : {}),
      } : {}),
    };
  }

  const createPath = templateMode ? "/tool-templates" : `/agents/${agentId}/tools`;
  const updatePath = templateMode ? `/tool-templates/${tool?.id}` : `/agents/${agentId}/tools/${tool?.id}`;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tool-templates"] });
    if (!templateMode) {
      qc.invalidateQueries({ queryKey: ["agent-tools", agentId] });
      // Tool faz parte do snapshot publicado → sinaliza "há alterações não publicadas".
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
    }
  };
  const saveError = (e: any) => setFormError(e?.payload?.detail ?? "Erro ao salvar a tool de estoque.");
  const createMut = useMutation({
    mutationFn: () => api.post(createPath, payload()),
    onSuccess: () => { invalidate(); onClose(); },
    onError: saveError,
  });
  const updateMut = useMutation({
    mutationFn: () => api.patch(updatePath, payload()),
    onSuccess: () => { invalidate(); onClose(); },
    onError: saveError,
  });

  const canSave = !!form.name.trim() && (templateMode || !!form.loja.trim()) && !(templateMode && tool && form.is_global && !form.version_note.trim());
  const saving = createMut.isPending || updateMut.isPending;

  // Teste real: roda o handler nativo na API com o estado ATUAL do form (mesmo
  // não-salvo) e o Valkey de produção — o cache usado/criado é o da loja.
  const [testCriterio, setTestCriterio] = useState("");
  const [testOut, setTestOut] = useState<{ result: string; tokens: Record<string, number>; logs: string; error: string | null } | null>(null);
  const testMut = useMutation({
    mutationFn: () =>
      api.post<{ result: string; tokens: Record<string, number>; logs: string; error: string | null }>(
        `/agents/${agentId}/tools/${tool?.id}/test`,
        {
          sample_args: { criterio: testCriterio },
          prompts: {
            criterio: form.criterio,
            selecionar: form.selecionar,
            detalhe: form.detalhe,
            apresentar: form.apresentar,
            instrucoes: form.instrucoes,
          },
          params: { loja: form.loja.trim() },
        },
      ),
    onSuccess: (r) => setTestOut(r),
    onError: (e: any) => setTestOut({ result: "", tokens: {}, logs: "", error: e?.payload?.detail ?? "Erro ao testar." }),
  });

  return (
    <div
      {...backdropClose(() => { requestClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <DialogPanel
        onClose={requestClose}
        label={templateMode ? "Template da Tool de Estoque" : "Tool de Estoque"}
        style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 680, maxWidth: "95vw", maxHeight: "88vh", padding: 24, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            {templateMode ? "Template da Tool de Estoque" : tool ? "Editar tool de estoque" : "Nova tool de estoque"}
          </span>
          <button aria-label="Fechar" onClick={requestClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>

        <div>
          <label style={labelStyle}>Nome</label>
          <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex.: busca_estoque" />
        </div>
        <div>
          <label style={labelStyle}>Descrição (o que o agente principal lê sobre a tool)</label>
          <textarea style={{ ...inputStyle, height: 56, resize: "vertical", lineHeight: 1.5 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} spellCheck={false} />
        </div>
        <div>
          <label style={labelStyle}>Loja (slug do estoque público)</label>
          <input style={inputStyle} value={form.loja} onChange={(e) => setForm({ ...form, loja: e.target.value })} placeholder={templateMode ? "{{ sistema.estoque }}" : "ex.: ab-seminovos"} />
          <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
            {templateMode
              ? "Para o self-service use {{ sistema.estoque }}: vira a loja que o cliente conectar no portal. Aceita {{variáveis}}."
              : "O mesmo slug de /estoque-public/{loja}. Com modelo Gemini, o estoque desta loja é cacheado e compartilhado entre os agentes (janela 7h–20h)."}
          </span>
        </div>
        {templateMode && (
          <div style={{ background: "color-mix(in srgb, var(--warn-soft), transparent 55%)", border: "1px solid var(--warn-line)", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, color: "var(--ink)", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })} style={{ accentColor: "var(--brand)" }} />
              Tool global · campos editáveis <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>· importada no agente global, vira sessão de bloco no portal; o resto segue este modelo ao vivo</span>
            </label>
            {form.is_global && (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
                  {ESTOQUE_EDITABLE.map((e) => (
                    <label key={e.path} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink)", cursor: "pointer" }}>
                      <input type="checkbox" checked={form.editable_fields.includes(e.path)} style={{ accentColor: "var(--brand)" }}
                        onChange={(ev) => setForm({ ...form, editable_fields: ev.target.checked ? [...form.editable_fields, e.path] : form.editable_fields.filter((p) => p !== e.path) })} />
                      {e.label}
                    </label>
                  ))}
                </div>
                {tool && (
                  <div>
                    <label style={labelStyle}>Nota da versão (obrigatória ao alterar um modelo global)</label>
                    <input style={inputStyle} value={form.version_note} onChange={(e) => setForm({ ...form, version_note: e.target.value })} placeholder="O que mudou nesta versão" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {PROMPT_FIELDS.map((f) => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}</label>
            <textarea
              style={{ ...inputStyle, height: f.rows * 18, resize: "vertical", lineHeight: 1.5, fontFamily: "var(--font-mono)", fontSize: 12 }}
              value={form[f.key] as string}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              spellCheck={false}
            />
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{f.hint}</span>
          </div>
        ))}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={labelStyle}>Modelo das análises internas desta tool</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => setForm({ ...form, model_provider_override: null, model_name_override: null, model_temperature_override: null, model_reasoning_effort_override: null })}
              style={{ flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", border: `1px solid ${overrideOn ? "var(--line-strong)" : "var(--brand)"}`, background: overrideOn ? "var(--surface-input)" : "var(--brand-soft)", color: "var(--ink)" }}
            >
              Usar modelo do Agente
            </button>
            <button
              type="button"
              onClick={() => {
                if (overrideOn) return;
                const first = modelCatalog[0];
                if (!first) return;
                setForm({ ...form, model_provider_override: first.provider, model_name_override: first.model_name, model_temperature_override: 0.7, model_reasoning_effort_override: null });
              }}
              style={{ flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", border: `1px solid ${overrideOn ? "var(--brand)" : "var(--line-strong)"}`, background: overrideOn ? "var(--brand-soft)" : "var(--surface-input)", color: "var(--ink)" }}
            >
              Escolher outro
            </button>
          </div>
          {overrideOn && (
            <ModelSelector
              compact
              showMaxTokens={false}
              value={{
                provider: form.model_provider_override ?? "",
                model_name: form.model_name_override ?? "",
                temperature: form.model_temperature_override ?? 0.7,
                reasoning_effort: form.model_reasoning_effort_override ?? "off",
              }}
              onChange={(next) =>
                setForm({
                  ...form,
                  model_provider_override: next.provider,
                  model_name_override: next.model_name,
                  model_temperature_override: next.temperature,
                  model_reasoning_effort_override: next.reasoning_effort === "off" ? null : next.reasoning_effort,
                })
              }
            />
          )}
          {overrideOn && (
            <ModelFallbackSection
              value={form.model_fallback}
              onChange={(next) => setForm({ ...form, model_fallback: next })}
              hint="Opcional. Usado se o modelo acima falhar."
            />
          )}
          <span style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.6 }}>
            O cache de estoque só funciona com modelos Gemini — com outro provider a tool roda normal, sem cache (motivo aparece no log de execução).
          </span>
        </div>

        <div>
          <label style={labelStyle}>Timeout (segundos · vazio = 60)</label>
          <input
            type="number"
            min={1}
            style={{ ...inputStyle, width: 140 }}
            value={form.timeout ?? ""}
            onChange={(e) => setForm({ ...form, timeout: e.target.value === "" ? null : Math.max(1, Number(e.target.value) || 1) })}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={labelStyle}>Limite de uso (por cliente)</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...inputStyle, width: 220 }}
              value={form.cooldown_seconds == null ? "none" : form.cooldown_seconds < 0 ? "flat" : "cooldown"}
              onChange={(e) => {
                const m = e.target.value;
                setForm({ ...form, cooldown_seconds: m === "none" ? null : m === "flat" ? -1 : 3600 });
              }}
            >
              <option value="none">Sem limite</option>
              <option value="flat">Número fixo de vezes por cliente</option>
              <option value="cooldown">A cada N segundos</option>
            </select>
            {form.cooldown_seconds != null && form.cooldown_seconds < 0 && (
              <>
                <input type="number" min={1} style={{ ...inputStyle, width: 120 }} value={-form.cooldown_seconds}
                  onChange={(e) => setForm({ ...form, cooldown_seconds: -Math.max(1, Number(e.target.value) || 1) })} />
                <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>vezes por cliente</span>
              </>
            )}
            {form.cooldown_seconds != null && form.cooldown_seconds > 0 && (
              <>
                <input type="number" min={1} style={{ ...inputStyle, width: 120 }} value={form.cooldown_seconds}
                  onChange={(e) => setForm({ ...form, cooldown_seconds: Math.max(1, Number(e.target.value) || 1) })} />
                <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>segundos</span>
              </>
            )}
          </div>
          {form.cooldown_seconds != null && (
            <textarea
              value={form.cooldown_message}
              onChange={(e) => setForm({ ...form, cooldown_message: e.target.value })}
              placeholder="Mensagem que o agente recebe quando a tool está bloqueada. Vazio = padrão."
              spellCheck={false}
              style={{ ...inputStyle, height: 56, resize: "vertical", lineHeight: 1.5 }}
            />
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
            <input type="checkbox" checked={form.qualifies_human} onChange={(e) => setForm({ ...form, qualifies_human: e.target.checked })} />
            Estratégica: qualifica lead para humano
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
            <input type="checkbox" checked={form.ends_followup} onChange={(e) => setForm({ ...form, ends_followup: e.target.checked })} />
            Esta tool encerra o follow-up
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={labelStyle}>Pausar IA após responder</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              min={0}
              max={1440}
              placeholder="Desligado"
              style={{ ...inputStyle, width: 120 }}
              value={form.pause_minutes ?? ""}
              onChange={(e) => setForm({ ...form, pause_minutes: Math.min(1440, Math.max(0, Number(e.target.value) || 0)) || null })}
            />
            <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>minutos (vazio = desligado)</span>
          </div>
        </div>

        {tool && !templateMode && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
            <label style={labelStyle}>Testar (execução real — usa/cria o cache de produção da loja)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={testCriterio}
                onChange={(e) => setTestCriterio(e.target.value)}
                placeholder="ex.: Estou buscando um SUV automático até 120 mil"
              />
              <button
                type="button"
                disabled={!testCriterio.trim() || testMut.isPending}
                onClick={() => { setTestOut(null); testMut.mutate(); }}
                style={{ background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {testMut.isPending ? "Testando..." : "Testar"}
              </button>
            </div>
            {testOut && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {testOut.error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{testOut.error}</span>}
                {testOut.logs && (
                  <span style={{ fontSize: 11, color: "var(--ink-muted)", fontFamily: "var(--font-mono)" }}>{testOut.logs}</span>
                )}
                {Object.keys(testOut.tokens ?? {}).length > 0 && (
                  <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                    Tokens — input: {testOut.tokens.prompt ?? 0} · cache: {testOut.tokens.cached ?? 0} · output: {testOut.tokens.completion ?? 0}
                  </span>
                )}
                {testOut.result && (
                  <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 6, padding: 8, maxHeight: 280, overflowY: "auto" }}>
                    {testOut.result}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}

        {formError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{formError}</span>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={requestClose} style={{ background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button
            disabled={!canSave || saving}
            onClick={() => { setFormError(""); (tool ? updateMut : createMut).mutate(); }}
            style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: canSave ? "pointer" : "not-allowed", opacity: !canSave || saving ? 0.6 : 1 }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
        {confirmClose && (
          <ConfirmDialog
            title="Descartar alterações?"
            confirmLabel="Descartar"
            danger
            onConfirm={() => { setConfirmClose(false); onClose(); }}
            onCancel={() => setConfirmClose(false)}
          />
        )}
      </DialogPanel>
    </div>
  );
}

export function ConfigEstoque({ agent }: { agent: any }) {
  const qc = useQueryClient();
  // creating: aberto com template=null (em branco) ou pré-preenchido de um template.
  const [creating, setCreating] = useState<{ template: any | null } | null>(null);
  const [picker, setPicker] = useState(false);
  const [editingTool, setEditingTool] = useState<any | null>(null);
  const [delTool, setDelTool] = useState<any | null>(null);

  const { data: allTools = [] } = useQuery<any[]>({
    queryKey: ["agent-tools", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/tools`),
  });
  const tools = allTools.filter((t: any) => t.kind === "estoque");

  // Templates de estoque (painel Dev) — importáveis só aqui, nunca no picker geral.
  const { data: templates = [] } = useQuery<any[]>({
    queryKey: ["tool-templates"],
    queryFn: () => api.get("/tool-templates"),
  });
  const estoqueTemplates = templates.filter((t: any) => t.kind === "estoque");

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["agent-tools", agent.id] }); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); };
  const deleteMut = useMutation({
    mutationFn: (toolId: string) => api.delete(`/agents/${agent.id}/tools/${toolId}`),
    onSuccess: invalidate,
  });
  const pauseMut = useMutation({
    mutationFn: ({ toolId, enabled }: { toolId: string; enabled: boolean }) =>
      api.patch(`/agents/${agent.id}/tools/${toolId}`, { enabled }),
    onSuccess: invalidate,
  });
  const dupMut = useMutation({
    mutationFn: (toolId: string) => api.post(`/agents/${agent.id}/tools/${toolId}/duplicate`, {}),
    onSuccess: invalidate,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>
          {tools.length} tool{tools.length !== 1 ? "s" : ""} de estoque
        </span>
        <button
          onClick={() => (estoqueTemplates.length ? setPicker(true) : setCreating({ template: null }))}
          style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}
        >
          + Nova tool de estoque
        </button>
      </div>

      {tools.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--line-max)", fontSize: 13, border: "1px dashed var(--line)", borderRadius: 8 }}>
          Nenhuma tool de estoque configurada ainda. A tool nativa faz busca, ficha e fotos do estoque da loja — com cache Gemini compartilhado.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tools.map((tool: any) => (
            <div
              key={tool.id}
              onClick={() => setEditingTool(tool)}
              style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}
            >
              <div style={{ flex: 1, minWidth: 0, opacity: tool.enabled === false ? 0.5 : 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>
                  {tool.name}
                  {tool.enabled === false && (
                    <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 7px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)" }}>
                      pausada
                    </span>
                  )}
                </div>
                {tool.description && (
                  <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>{tool.description}</div>
                )}
                <div style={{ fontSize: 11, color: "var(--line-max)", marginTop: 6, fontFamily: "var(--font-mono)" }}>
                  loja: {tool.params?.loja ?? "—"}
                </div>
              </div>
              <button
                title="Duplicar (cria cópia pausada)"
                disabled={dupMut.isPending}
                onClick={(e) => { e.stopPropagation(); dupMut.mutate(tool.id); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
              </button>
              <button
                title={tool.enabled === false ? "Reativar tool" : "Pausar tool (o agente perde acesso)"}
                disabled={pauseMut.isPending}
                onClick={(e) => { e.stopPropagation(); pauseMut.mutate({ toolId: tool.id, enabled: tool.enabled === false }); }}
                style={{ background: "none", border: "none", color: tool.enabled === false ? "var(--brand)" : "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
              >
                {tool.enabled === false ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                )}
              </button>
              <button
                title="Excluir tool"
                disabled={deleteMut.isPending}
                onClick={(e) => { e.stopPropagation(); setDelTool(tool); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {picker && (
        <div
          {...backdropClose(() => { setPicker(false); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <DialogPanel onClose={() => setPicker(false)} label="Nova tool de estoque" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 480, maxWidth: "95vw", maxHeight: "80vh", padding: 24, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Nova tool de estoque</span>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Comece em branco ou importe um template de estoque (só a loja fica por sua conta).</span>
            <button
              onClick={() => { setPicker(false); setCreating({ template: null }); }}
              style={{ textAlign: "left", background: "var(--surface-input)", border: "1px dashed var(--line-strong)", borderRadius: 8, padding: "12px 14px", cursor: "pointer", fontSize: 13, color: "var(--ink)" }}
            >
              Em branco
            </button>
            {estoqueTemplates.map((tpl: any) => (
              <button
                key={tpl.id}
                title={tpl.template_description || tpl.description || ""}
                onClick={() => { setPicker(false); setCreating({ template: tpl }); }}
                style={{ textAlign: "left", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "12px 14px", cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", display: "block" }}>{tpl.name}</span>
                {tpl.description && <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{tpl.description}</span>}
              </button>
            ))}
          </DialogPanel>
        </div>
      )}
      {creating && (
        <EstoqueToolModal agentId={agent.id} template={creating.template ?? undefined} onClose={() => setCreating(null)} />
      )}
      {editingTool && (
        <EstoqueToolModal key={editingTool.id} agentId={agent.id} tool={editingTool} onClose={() => setEditingTool(null)} />
      )}
      {delTool && (
        <ConfirmDialog
          title={`Excluir "${delTool.name}"?`}
          confirmLabel="Excluir"
          danger
          onConfirm={() => { deleteMut.mutate(delTool.id); setDelTool(null); }}
          onCancel={() => setDelTool(null)}
        />
      )}
    </div>
  );
}
